import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, CircularProgress, Alert
} from '@mui/material';
import { createWorkSchedule, updateWorkSchedule, deleteWorkSchedule } from '../../collections/workSchedules';

const SHIFT_LABELS = {
  MORNING: 'Ca sáng (7:30 - 12:00)',
  AFTERNOON: 'Ca chiều (13:00 - 18:30)'
};

/**
 * ScheduleEntryForm - form for creating/editing schedule entries
 * @param {boolean} open - whether dialog is open
 * @param {function} onClose - callback when dialog closes (saved: boolean)
 * @param {object} cellData - cell context { accountId, accountName, date, shift, schedule }
 * @param {array} dutyTypes - duty types from API { id, code, name_vi, color }
 */
const ScheduleEntryForm = ({ open, onClose, cellData, dutyTypes }) => {
  const [dutyTypeId, setDutyTypeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isEditMode = !!cellData?.schedule?.id;

  useEffect(() => {
    if (open) {
      if (cellData?.schedule) {
        setDutyTypeId(cellData.schedule.duty_type_id || '');
      } else {
        setDutyTypeId('');
      }
      setError(null);
      setConfirmDelete(false);
    }
  }, [open, cellData]);

  const handleSubmit = async () => {
    if (!dutyTypeId) {
      setError('Vui lòng chọn loại công việc');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isEditMode) {
        await updateWorkSchedule(cellData.schedule.id, {
          duty_type_id: dutyTypeId
        });
      } else {
        await createWorkSchedule({
          account_id: cellData.accountId,
          schedule_date: cellData.date,
          shift: cellData.shift,
          duty_type_id: dutyTypeId
        });
      }
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Có lỗi xảy ra. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError(null);

    try {
      await deleteWorkSchedule(cellData.schedule.id);
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Có lỗi xảy ra khi xóa.');
    } finally {
      setLoading(false);
      setConfirmDelete(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose(false);
    }
  };

  if (!cellData) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditMode ? 'Chỉnh sửa lịch làm việc' : 'Thêm lịch làm việc'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {/* Context Info (Read-only) */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Nhân viên
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {cellData.accountName || `Account #${cellData.accountId}`}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Ngày
            </Typography>
            <Typography variant="body1">
              {cellData.date}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Ca làm việc
            </Typography>
            <Typography variant="body1">
              {SHIFT_LABELS[cellData.shift]}
            </Typography>
          </Box>

          {/* Duty Type Selection */}
          <FormControl fullWidth>
            <InputLabel>Loại công việc</InputLabel>
            <Select
              value={dutyTypeId}
              label="Loại công việc"
              onChange={(e) => setDutyTypeId(e.target.value)}
              disabled={loading}
            >
              {dutyTypes.map(type => (
                <MenuItem key={type.id} value={type.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: type.color || '#eeeeee'
                      }}
                    />
                    {type.name_vi}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Delete Confirmation */}
          {confirmDelete && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Bạn có chắc chắn muốn xóa lịch này?
              <Box sx={{ mt: 1 }}>
                <Button size="small" onClick={() => setConfirmDelete(false)}>
                  Hủy
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  Xác nhận xóa
                </Button>
              </Box>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        {isEditMode && !confirmDelete && (
          <Button
            color="error"
            onClick={() => setConfirmDelete(true)}
            disabled={loading}
            sx={{ mr: 'auto' }}
          >
            Xóa
          </Button>
        )}
        <Button onClick={handleClose} disabled={loading}>
          Hủy
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !dutyTypeId}
        >
          {loading ? <CircularProgress size={24} /> : 'Lưu'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleEntryForm;
